import type { AdintelligenceIndustryPerformanceOverTimeListData } from "@similarweb/pro-api-client";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useApiQuery } from "app/modules/api-client";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";
import type { DevicePerformanceData } from "app/pages/digital-marketing/ad-intelligence/common/overview/types";

import { useIndustryAvailabilityParams } from "../useIndustryAvailabilityParams";

const mapIndustryPerformanceData = (
    data?: AdintelligenceIndustryPerformanceOverTimeListData,
): DevicePerformanceData | undefined => {
    const totalDevice = data?.Total;
    if (!totalDevice) {
        return undefined;
    }
    return {
        daily: totalDevice.daily ?? {},
        weekly: totalDevice.weekly ?? {},
        monthly: totalDevice.monthly ?? {},
        total: {
            spend: totalDevice.total?.spend ?? 0,
            impressions: totalDevice.total?.impressions ?? 0,
            visits: totalDevice.total?.visits ?? 0,
        },
    };
};

const hasAdIntelMetrics = (data?: AdintelligenceIndustryPerformanceOverTimeListData): boolean =>
    Boolean(data?.Total?.total?.spend || data?.Total?.total?.impressions);

export const useIndustryPerformanceOverTime = (category: IFlattenedCategory) => {
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryPerformanceOverTimeList,
    );
    const { data, isLoading, isFetching, isError } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName: "adintelligenceIndustryPerformanceOverTimeList",
            options: { enabled: !availability.isFullyUnavailable },
        },
        params,
    );

    return {
        performanceData: mapIndustryPerformanceData(data),
        hasAdIntelMetrics: hasAdIntelMetrics(data),
        isWindow: params.isWindow,
        isLoading: isLoading || isFetching,
        isError,
        availability,
    };
};
